import { defineStore } from 'pinia'
import { apiService } from '@/services/api.js'
import { useAuthStore } from '@/stores/auth.js'

function sumGames(response, matchtypeKey) {
    const totals = response?.total_games ?? {}
    if (matchtypeKey) {
        return totals[matchtypeKey] ?? 0
    }
    return Object.values(totals).reduce((sum, count) => sum + count, 0)
}

/**
 * Profile statistics for the statistics pages. Every page reads from the same responses of
 * GET /api/v1/profile (all time plus the last three months), so moving between the statistics
 * home and a game mode page doesn't refetch them. `fetch()` keeps showing what is cached while it
 * refreshes in the background.
 */
export const useStatisticsStore = defineStore('statistics', {
    state: () => ({
        profileId: null,
        allTime: null,
        // Oldest first; the last entry is the current month.
        months: [],
        isLoading: false,
        hasError: false,
    }),

    getters: {
        /** Games for one match type, or over every match type when no key is given. */
        totalGames:
            (state) =>
            (matchtypeKey = null) =>
                sumGames(state.allTime, matchtypeKey),

        gamesPerMonth:
            (state) =>
            (matchtypeKey = null) =>
                state.months.map((month) => sumGames(month, matchtypeKey)),

        /** Games this month minus last month, signed for display ("+34", "-50", "0"). */
        gamesVsLastMonth() {
            return (matchtypeKey = null) => {
                const [previous = 0, current = 0] = this.gamesPerMonth(matchtypeKey).slice(-2)
                const delta = current - previous
                return delta > 0 ? `+${delta}` : String(delta)
            }
        },
    },

    actions: {
        async fetch() {
            const profileId = useAuthStore().profile?.id ?? null
            if (profileId !== this.profileId) {
                this.$reset()
                this.profileId = profileId
            }

            this.isLoading = this.allTime === null
            this.hasError = false

            const now = new Date()
            const monthStart = (offset) => {
                const month = new Date(now.getFullYear(), now.getMonth() - offset, 1)
                return `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-01`
            }

            try {
                const [allTime, ...months] = await Promise.all([
                    apiService.get('/api/v1/profile?period=all_time'),
                    ...[2, 1, 0].map((offset) =>
                        apiService.get(
                            `/api/v1/profile?period=month&month_start=${monthStart(offset)}`,
                        ),
                    ),
                ])

                this.allTime = allTime
                this.months = months
            } catch (error) {
                console.error('Failed to load statistics:', error)
                this.hasError = this.allTime === null
            } finally {
                this.isLoading = false
            }
        },
    },
})
